// Estado falso de Identity (modo fake, só em dev — Parte 0.6): usuários por escopo, sessões, clientes OIDC e
// redefinições de MFA, no formato do contrato (`Identity*Response` de schema.d.ts). Semeado por cenário de `/me`.
import type { MeResponse, components } from '@giyus/api-client';

export type IdentitySessionResponse = components['schemas']['IdentitySessionResponse'];
export type IdentityUserSummaryResponse = components['schemas']['IdentityUserSummaryResponse'];
export type IdentityInvitationSummaryResponse =
  components['schemas']['IdentityInvitationSummaryResponse'];
export type IdentityOidcClientResponse = components['schemas']['IdentityOidcClientResponse'];
export type IdentityMfaResetRequestResponse =
  components['schemas']['IdentityMfaResetRequestResponse'];

export interface FakeTemporaryGrant {
  readonly grantId: string;
  readonly requestedBy: string;
  readonly reason: string;
  readonly scopeSummary: string;
  extendedBy: string | null;
  extendedAt: string | null;
  extensionReason: string | null;
}

export interface FakeUser {
  readonly id: string;
  readonly kind: string;
  status: string;
  readonly email: string;
  readonly displayName: string;
  readonly preferredLocale: string;
  expiresAt: string | null;
  lockedUntil: string | null;
  failedAttemptCycle: number;
  readonly personId: string;
  /** Tenants que emitiram convite para este usuário (vínculo usuário↔tenant até a Autorização existir). */
  readonly invitationTenantIds: string[];
  pendingInvitation: IdentityInvitationSummaryResponse | null;
  readonly mfa: { enrolled: boolean; methods: string[] };
  sessions: IdentitySessionResponse[];
  grant: FakeTemporaryGrant | null;
}

export interface FakeOidcClient extends IdentityOidcClientResponse {
  status: string;
}

export interface FakeIdentityState {
  readonly users: FakeUser[];
  readonly clients: FakeOidcClient[];
  readonly mfaResetRequests: IdentityMfaResetRequestResponse[];
  readonly base: string;
  counter: number;
}

/** Instante de referência das fixtures: o momento do seed (datas relativas a ele ficam coerentes com o relógio real). */
export function fakeIdentityBase(): string {
  return new Date().toISOString();
}

export function fakeId(state: Pick<FakeIdentityState, 'counter'> & { counter: number }): string {
  state.counter += 1;
  return `01926f3e-0000-7000-8000-00000000f${state.counter.toString(16).padStart(3, '0')}`;
}

export function shiftIso(base: string, minutes: number): string {
  return new Date(new Date(base).getTime() + minutes * 60_000).toISOString();
}

function session(
  id: string,
  base: string,
  authMinutesAgo: number,
  deviceLabel: string,
  ipAddress: string,
): IdentitySessionResponse {
  return {
    id,
    authTime: shiftIso(base, -authMinutesAgo),
    lastActivityAt: shiftIso(base, -1),
    idleExpiresAt: shiftIso(base, 29),
    absoluteExpiresAt: shiftIso(base, 12 * 60 - authMinutesAgo),
    ipAddress,
    deviceLabel,
    isCurrent: false,
  };
}

function user(
  partial: Pick<FakeUser, 'id' | 'email' | 'displayName' | 'personId'> &
    Partial<Omit<FakeUser, 'id' | 'email' | 'displayName' | 'personId'>>,
): FakeUser {
  return {
    kind: 'Human',
    status: 'Active',
    preferredLocale: 'pt-BR',
    expiresAt: null,
    lockedUntil: null,
    failedAttemptCycle: 0,
    invitationTenantIds: [],
    pendingInvitation: null,
    mfa: { enrolled: true, methods: ['Totp', 'RecoveryCodes'] },
    sessions: [],
    grant: null,
    ...partial,
  };
}

/** Semeia o estado a partir de `/me`: o próprio usuário (com a sessão corrente) + usuários por tenant + clientes OIDC. */
export function seedIdentityState(me: MeResponse): FakeIdentityState {
  const base = fakeIdentityBase();
  const tenantIds = me.contexts.map((context) => context.tenantId);
  const pilot = tenantIds[0] ?? '01926f3e-0000-7000-8000-00000000000a';
  const second = tenantIds[1] ?? '01926f3e-0000-7000-8000-00000000000b';
  const state: FakeIdentityState = {
    base,
    counter: 0,
    users: [],
    clients: [],
    mfaResetRequests: [],
  };

  const currentSessionId = me.session?.id ?? '01926f3e-0000-7000-8000-0000000000f1';

  // Modo Greenfield de Produção (syllas@giyus.com.br): mantém exclusivamente o administrador full logado
  if (
    me.user.id === '01926f3e-0000-7000-8000-000000000099' ||
    me.user.email.toLowerCase() === 'syllas@giyus.com.br'
  ) {
    state.users.push(
      user({
        id: me.user.id,
        email: me.user.email,
        displayName: me.user.displayName,
        preferredLocale: me.user.preferredLocale,
        kind: me.user.kind,
        personId: me.person?.id ?? '01926f3e-0000-7000-8000-000000000099',
        invitationTenantIds: [...tenantIds],
        mfa: { enrolled: me.user.mfa.enrolled, methods: [...me.user.mfa.methods] },
        sessions: [session(currentSessionId, base, 240, 'Chrome · macOS', '203.0.113.10')],
      }),
    );
    state.clients.push({
      id: '01926f3e-0000-7000-8000-0000000000b1',
      clientId: 'giyus-web',
      displayName: 'GIYUS Web',
      kind: 'Public',
      status: 'Active',
      redirectUris: [
        'https://app.giyus.com.br/auth/callback',
        'https://app.giyus.com.br/auth/silent-callback',
      ],
      postLogoutRedirectUris: ['https://app.giyus.com.br/'],
      allowedScopes: ['openid', 'profile', 'email', 'giyus.api', 'offline_access'],
      allowedGrantTypes: ['authorization_code', 'refresh_token'],
      requirePkce: true,
      ownerUserId: null,
      secretExpiresAt: null,
    });
    return state;
  }

  state.users.push(
    user({
      id: me.user.id,
      email: me.user.email,
      displayName: me.user.displayName,
      preferredLocale: me.user.preferredLocale,
      kind: me.user.kind,
      personId: me.person?.id ?? '01926f3e-0000-7000-8000-0000000000p9',
      invitationTenantIds: [...tenantIds],
      mfa: { enrolled: me.user.mfa.enrolled, methods: [...me.user.mfa.methods] },
      sessions: [
        session(currentSessionId, base, 240, 'Chrome · Windows', '203.0.113.10'),
        session(
          '01926f3e-0000-7000-8000-0000000000f2',
          base,
          30,
          'Safari · iPhone',
          '198.51.100.7',
        ),
      ],
    }),
    user({
      id: '01926f3e-0000-7000-8000-0000000000d1',
      email: 'joana.invited@centro.example',
      displayName: 'Joana Pereira',
      personId: '01926f3e-0000-7000-8000-0000000000e1',
      status: 'Invited',
      invitationTenantIds: [pilot],
      mfa: { enrolled: false, methods: [] },
      pendingInvitation: {
        id: '01926f3e-0000-7000-8000-0000000000c1',
        issuedAt: shiftIso(base, -60),
        expiresAt: shiftIso(base, 71 * 60),
        tenantId: pilot,
      },
    }),
    user({
      id: '01926f3e-0000-7000-8000-0000000000d2',
      email: 'marcos.lima@centro.example',
      displayName: 'Marcos Lima',
      personId: '01926f3e-0000-7000-8000-0000000000e2',
      invitationTenantIds: [pilot],
      sessions: [
        session('01926f3e-0000-7000-8000-0000000000f3', base, 15, 'Edge · Windows', '192.0.2.44'),
      ],
    }),
    user({
      id: '01926f3e-0000-7000-8000-0000000000d3',
      email: 'paula.locked@centro.example',
      displayName: 'Paula Rocha',
      personId: '01926f3e-0000-7000-8000-0000000000e3',
      status: 'Active',
      lockedUntil: shiftIso(base, 45),
      failedAttemptCycle: 2,
      invitationTenantIds: [pilot],
    }),
    user({
      id: '01926f3e-0000-7000-8000-0000000000d4',
      email: 'rita.disabled@centro.example',
      displayName: 'Rita Nunes',
      personId: '01926f3e-0000-7000-8000-0000000000e4',
      status: 'Disabled',
      invitationTenantIds: [second],
    }),
    user({
      id: '01926f3e-0000-7000-8000-0000000000d5',
      email: 'monitor.temp@cro.example',
      displayName: 'Lucas Monitor (CRO)',
      personId: '01926f3e-0000-7000-8000-0000000000e5',
      kind: 'TemporaryHuman',
      preferredLocale: 'en-US',
      expiresAt: shiftIso(base, 30 * 24 * 60),
      invitationTenantIds: [second],
      grant: {
        grantId: '01926f3e-0000-7000-8000-0000000000a5',
        requestedBy: me.user.id,
        reason: 'Monitoria do estudo GX-201',
        scopeSummary: 'Somente leitura no estudo GX-201',
        extendedBy: null,
        extendedAt: null,
        extensionReason: null,
      },
    }),
    user({
      id: '01926f3e-0000-7000-8000-0000000000d6',
      email: 'workers@giyus.com.br',
      displayName: 'GIYUS Workers',
      personId: '01926f3e-0000-7000-8000-0000000000e6',
      kind: 'Service',
      preferredLocale: 'en-US',
      mfa: { enrolled: false, methods: [] },
    }),
  );

  state.clients.push(
    {
      id: '01926f3e-0000-7000-8000-0000000000b1',
      clientId: 'giyus-web',
      displayName: 'GIYUS Web',
      kind: 'Public',
      status: 'Active',
      redirectUris: [
        'https://app-dev.giyus.com.br/auth/callback',
        'https://app-dev.giyus.com.br/auth/silent-callback',
      ],
      postLogoutRedirectUris: ['https://app-dev.giyus.com.br/'],
      allowedScopes: ['openid', 'profile', 'email', 'giyus.api', 'offline_access'],
      allowedGrantTypes: ['authorization_code', 'refresh_token'],
      requirePkce: true,
      ownerUserId: null,
      secretExpiresAt: null,
    },
    {
      id: '01926f3e-0000-7000-8000-0000000000b2',
      clientId: 'giyus-workers',
      displayName: 'GIYUS Workers',
      kind: 'Confidential',
      status: 'Active',
      redirectUris: [],
      postLogoutRedirectUris: [],
      allowedScopes: ['giyus.api'],
      allowedGrantTypes: ['client_credentials'],
      requirePkce: false,
      ownerUserId: me.user.id,
      secretExpiresAt: shiftIso(base, 150 * 24 * 60),
    },
  );

  return state;
}

export function toSummary(entry: FakeUser): IdentityUserSummaryResponse {
  return {
    id: entry.id,
    kind: entry.kind,
    status: entry.status,
    email: entry.email,
    displayName: entry.displayName,
    preferredLocale: entry.preferredLocale,
    expiresAt: entry.expiresAt,
    lockedUntil: entry.lockedUntil,
  };
}
